// Procedural circuit furniture from a Track centreline: edges, white lines,
// kerbs, run-off, barriers, start/finish and sectors, plus the surface lookup
// the physics uses. Pure functions, no Babylon: everything is plain numbers
// and typed arrays so it can be unit-tested and reused by physics and render.
//
// Conventions (same as track.ts): x east, z north, metres; y up for meshes.
// "Right" is the right-hand side of the driving direction, normal (tz, -tx).
// Lateral distances per side are measured outward from the centreline and
// are always positive; signed offsets are positive to the right.

import type { Track, TrackProjection } from './track';

export type Surface = 'track' | 'kerb' | 'runoff-asphalt' | 'grass' | 'gravel' | 'outside';
export type Side = 'left' | 'right';

export interface Vec2 {
  x: number;
  z: number;
}

export interface LayoutOptions {
  /** Kerb width beyond the track edge, metres. */
  kerbWidth: number;
  /** Length of one red or white kerb block, metres. */
  kerbBlock: number;
  /** White edge line width (painted just inside the track edge), metres. */
  lineWidth: number;
  /** Barrier wall height, metres. */
  barrierHeight: number;
  /** Run-off beyond the edge on straights, metres. */
  straightRunoff: number;
  /** Run-off beyond the edge on the inside of corners, metres. */
  insideRunoff: number;
  /** Run-off beyond the edge on the outside of corners, metres. */
  outsideRunoff: number;
  /** Grass verge between the run-off and the barrier, metres. */
  verge: number;
  /** Minimum gap kept between a barrier and the local medial axis (where it would meet another bit of track), metres. */
  barrierMargin: number;
  /** Search limit for free space beside the track, metres. */
  maxClearance: number;
}

export const DEFAULT_LAYOUT_OPTIONS: Readonly<LayoutOptions> = {
  kerbWidth: 1.2,
  kerbBlock: 5,
  lineWidth: 0.2,
  barrierHeight: 1.0,
  straightRunoff: 10,
  insideRunoff: 14,
  outsideRunoff: 26,
  verge: 3,
  barrierMargin: 2,
  maxClearance: 70,
};

export interface Corner {
  /** Apex (peak curvature) point index. */
  apex: number;
  /** Distance of the apex from the start line, metres. */
  s: number;
  /** First and last point index of the corner. `end` may exceed `count` (wraps). */
  start: number;
  end: number;
  direction: Side;
  /** Smallest radius of the (smoothed) centreline, metres. */
  minRadius: number;
  /** Total heading change, radians (positive). */
  turnAngle: number;
  /** Slow corner at the end of a long straight. */
  heavyBraking: boolean;
}

export interface KerbStrip {
  side: Side;
  kind: 'apex' | 'exit';
  /** Point index range; the kerb covers segments start..end-1. `end` may exceed `count` (wraps). */
  start: number;
  end: number;
  /** Distance along the centreline where the strip starts, and its length, metres. */
  s0: number;
  length: number;
}

/** Per-side lateral profile, one value per centreline point unless noted. */
export interface SideProfile {
  /** Track edge (half width on this side). */
  edge: Float32Array;
  /** 1 where segment i..i+1 has a kerb on this side. */
  kerb: Uint8Array;
  /** Outer limit of the paved run-off. */
  asphaltOuter: Float32Array;
  /** Outer limit of the run-off (beyond asphaltOuter it is grass or gravel). */
  runoffOuter: Float32Array;
  /** Per segment: 1 if the band asphaltOuter..runoffOuter is gravel, else grass. */
  gravel: Uint8Array;
  /** Barrier line (grass verge between runoffOuter and here). */
  barrier: Float32Array;
  /** Free space beside the centreline before meeting another part of the track (medial axis). */
  clearance: Float32Array;
}

export interface StartLine {
  s: number;
  x: number;
  z: number;
  heading: number;
  widthLeft: number;
  widthRight: number;
}

export interface TrackLayout {
  track: Track;
  options: LayoutOptions;
  count: number;
  length: number;
  /** Cumulative distance per point; cum[count] = length. */
  cum: Float64Array;
  /** Smoothed unit tangent and right normal (tz, -tx) per point. */
  tx: Float32Array;
  tz: Float32Array;
  /** Smoothed signed curvature per point, 1/m; positive = turning right. */
  curvature: Float32Array;
  corners: Corner[];
  kerbs: KerbStrip[];
  right: SideProfile;
  left: SideProfile;
  startLine: StartLine;
  /** Distances of the two internal sector boundaries (sector 1 = [0, s1), 2 = [s1, s2), 3 = [s2, L)). */
  sectors: [number, number];
  edges: { left: Vec2[]; right: Vec2[] };
  /** Closed barrier polylines, cleaned of self-intersections. */
  barriers: { left: Vec2[]; right: Vec2[] };
}

/** One renderable group: indexed triangles with per-vertex data. */
export interface MeshData {
  /** x, y, z per vertex. */
  positions: Float32Array;
  /** x, y, z per vertex. */
  normals: Float32Array;
  /** Triangle indices, wound counter-clockwise seen from the front (Babylon's default front face). */
  indices: Uint32Array;
  /** u, v per vertex in metres (u lateral, v along the track), except kerbs (u 0..1 across, v in kerb blocks). */
  uvs: Float32Array;
  /** r, g, b, a per vertex (kerbs only). */
  colors?: Float32Array;
}

export interface TrackGeometry {
  road: MeshData;
  lines: MeshData;
  kerbs: MeshData;
  runoffAsphalt: MeshData;
  grass: MeshData;
  gravel: MeshData;
  barriers: MeshData;
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
}

/** Height of each flat layer, metres, so overlapping layers don't z-fight. */
export const LAYER_Y = {
  grass: 0,
  gravel: 0.004,
  runoffAsphalt: 0.008,
  road: 0.012,
  kerbs: 0.016,
  lines: 0.02,
} as const;

export const KERB_RED: readonly [number, number, number] = [0.78, 0.07, 0.06];
export const KERB_WHITE: readonly [number, number, number] = [0.93, 0.93, 0.92];

// ---------------------------------------------------------------------------
// Small helpers

const wrapAngle = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));
const mod = (i: number, n: number): number => ((i % n) + n) % n;

/** Circular box filter, radius r points. */
function boxCircular(src: ArrayLike<number>, r: number): Float32Array {
  const n = src.length;
  const out = new Float32Array(n);
  if (r <= 0) {
    for (let i = 0; i < n; i++) out[i] = src[i]!;
    return out;
  }
  let sum = 0;
  for (let k = -r; k <= r; k++) sum += src[mod(k, n)]!;
  const w = 2 * r + 1;
  for (let i = 0; i < n; i++) {
    out[i] = sum / w;
    sum += src[mod(i + r + 1, n)]! - src[mod(i - r, n)]!;
  }
  return out;
}

/** Circular running minimum, radius r points. */
function minCircular(src: ArrayLike<number>, r: number): Float32Array {
  const n = src.length;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let m = Infinity;
    for (let k = -r; k <= r; k++) m = Math.min(m, src[mod(i + k, n)]!);
    out[i] = m;
  }
  return out;
}

/** Smooth while never exceeding the input anywhere: box(min(x)). */
function smoothBelow(src: ArrayLike<number>, r: number): Float32Array {
  return boxCircular(minCircular(src, r), r);
}

/** Uniform grid over polyline segments for nearest-distance queries. */
class SegmentGrid {
  private readonly cells = new Map<number, number[]>();
  constructor(
    private readonly xs: Float64Array,
    private readonly zs: Float64Array,
    private readonly cell: number,
  ) {
    const n = xs.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const x0 = Math.floor(Math.min(xs[i]!, xs[j]!) / cell);
      const x1 = Math.floor(Math.max(xs[i]!, xs[j]!) / cell);
      const z0 = Math.floor(Math.min(zs[i]!, zs[j]!) / cell);
      const z1 = Math.floor(Math.max(zs[i]!, zs[j]!) / cell);
      for (let cx = x0; cx <= x1; cx++)
        for (let cz = z0; cz <= z1; cz++) {
          const key = this.key(cx, cz);
          let list = this.cells.get(key);
          if (!list) this.cells.set(key, (list = []));
          list.push(i);
        }
    }
  }
  private key(cx: number, cz: number): number {
    return (cx + 32768) * 65536 + (cz + 32768);
  }
  /** Distance from (x, z) to the closed polyline, searching up to radius r (returns >= r if nothing nearer). */
  nearest(x: number, z: number, r: number): number {
    const { xs, zs, cell } = this;
    const n = xs.length;
    let best = r * r;
    const cx0 = Math.floor((x - r) / cell);
    const cx1 = Math.floor((x + r) / cell);
    const cz0 = Math.floor((z - r) / cell);
    const cz1 = Math.floor((z + r) / cell);
    for (let cx = cx0; cx <= cx1; cx++)
      for (let cz = cz0; cz <= cz1; cz++) {
        const list = this.cells.get(this.key(cx, cz));
        if (!list) continue;
        for (const i of list) {
          const j = (i + 1) % n;
          const ax = xs[i]!;
          const az = zs[i]!;
          const dx = xs[j]! - ax;
          const dz = zs[j]! - az;
          const len2 = dx * dx + dz * dz || 1;
          const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / len2));
          const px = ax + dx * t - x;
          const pz = az + dz * t - z;
          const d2 = px * px + pz * pz;
          if (d2 < best) best = d2;
        }
      }
    return Math.sqrt(best);
  }
}

/** Proper intersection of segments ab and cd; returns the parameter on ab or -1. */
function segIntersect(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  cx: number,
  cz: number,
  dx: number,
  dz: number,
): number {
  const rx = bx - ax;
  const rz = bz - az;
  const sx = dx - cx;
  const sz = dz - cz;
  const den = rx * sz - rz * sx;
  if (Math.abs(den) < 1e-12) return -1;
  const qx = cx - ax;
  const qz = cz - az;
  const t = (qx * sz - qz * sx) / den;
  const u = (qx * rz - qz * rx) / den;
  return t > 1e-9 && t < 1 - 1e-9 && u > 1e-9 && u < 1 - 1e-9 ? t : -1;
}

/** Index pairs (i, j) of non-adjacent segments of a closed polyline that cross. */
export function selfIntersections(poly: readonly Vec2[]): [number, number][] {
  const n = poly.length;
  const out: [number, number][] = [];
  // Sort-and-sweep on x to stay well under O(n^2) in practice.
  const order = Array.from({ length: n }, (_, i) => i);
  const minX = (i: number): number => Math.min(poly[i]!.x, poly[(i + 1) % n]!.x);
  const maxX = (i: number): number => Math.max(poly[i]!.x, poly[(i + 1) % n]!.x);
  order.sort((a, b) => minX(a) - minX(b));
  for (let oi = 0; oi < n; oi++) {
    const i = order[oi]!;
    const ixMax = maxX(i);
    const a = poly[i]!;
    const b = poly[(i + 1) % n]!;
    for (let oj = oi + 1; oj < n; oj++) {
      const j = order[oj]!;
      if (minX(j) > ixMax) break;
      if (j === i || (j + 1) % n === i || (i + 1) % n === j) continue;
      const c = poly[j]!;
      const d = poly[(j + 1) % n]!;
      if (segIntersect(a.x, a.z, b.x, b.z, c.x, c.z, d.x, d.z) >= 0) out.push(i < j ? [i, j] : [j, i]);
    }
  }
  return out;
}

/** Count crossings between two polylines (closed). */
export function crossings(p: readonly Vec2[], q: readonly Vec2[]): number {
  const n = p.length;
  const m = q.length;
  let count = 0;
  const qOrder = Array.from({ length: m }, (_, j) => j);
  const qMin = (j: number): number => Math.min(q[j]!.x, q[(j + 1) % m]!.x);
  qOrder.sort((a, b) => qMin(a) - qMin(b));
  const qMins = qOrder.map(qMin);
  for (let i = 0; i < n; i++) {
    const a = p[i]!;
    const b = p[(i + 1) % n]!;
    const lo = Math.min(a.x, b.x);
    const hi = Math.max(a.x, b.x);
    for (let k = 0; k < m && qMins[k]! <= hi; k++) {
      const j = qOrder[k]!;
      const c = q[j]!;
      const d = q[(j + 1) % m]!;
      if (Math.max(c.x, d.x) < lo) continue;
      if (segIntersect(a.x, a.z, b.x, b.z, c.x, c.z, d.x, d.z) >= 0) count++;
    }
  }
  return count;
}

/** Cut out loops from a closed polyline until it is simple (removes the shorter side of each crossing). */
export function removeLoops(poly: Vec2[]): Vec2[] {
  let pts = poly.slice();
  for (let guard = 0; guard < 200; guard++) {
    const hits = selfIntersections(pts);
    if (hits.length === 0) break;
    const n = pts.length;
    // Resolve the crossing with the shortest loop first.
    let bestK = 0;
    let bestSpan = Infinity;
    hits.forEach(([i, j], k) => {
      const span = Math.min(j - i, n - (j - i));
      if (span < bestSpan) {
        bestSpan = span;
        bestK = k;
      }
    });
    const [i, j] = hits[bestK]!;
    const a = pts[i]!;
    const b = pts[(i + 1) % n]!;
    const c = pts[j]!;
    const d = pts[(j + 1) % n]!;
    const t = segIntersect(a.x, a.z, b.x, b.z, c.x, c.z, d.x, d.z);
    const p = { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
    if (j - i <= n - (j - i)) {
      // Drop i+1..j, keep the rest.
      pts = [...pts.slice(0, i + 1), p, ...pts.slice(j + 1)];
    } else {
      // The loop wraps through index 0: keep i+1..j only.
      pts = [p, ...pts.slice(i + 1, j + 1)];
    }
  }
  return pts;
}

// ---------------------------------------------------------------------------
// Layout

const cache = new WeakMap<Track, TrackLayout>();

/** Precomputed layout for a track with default options (cached per Track instance). */
export function getLayout(track: Track): TrackLayout {
  let l = cache.get(track);
  if (!l) {
    l = buildLayout(track);
    cache.set(track, l);
  }
  return l;
}

/** Build a layout (uncached). Use getLayout() unless you need non-default options. */
export function buildLayout(track: Track, opts: Partial<LayoutOptions> = {}): TrackLayout {
  const o: LayoutOptions = { ...DEFAULT_LAYOUT_OPTIONS, ...opts };
  const pts = track.points;
  const n = pts.length;
  const xs = new Float64Array(n);
  const zs = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    xs[i] = pts[i]!.x;
    zs[i] = pts[i]!.z;
  }

  // Segments and cumulative distance.
  const segLen = new Float64Array(n);
  const segHd = new Float64Array(n);
  const cum = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = xs[j]! - xs[i]!;
    const dz = zs[j]! - zs[i]!;
    segLen[i] = Math.hypot(dx, dz);
    segHd[i] = Math.atan2(dx, dz);
    cum[i + 1] = cum[i]! + segLen[i]!;
  }
  const L = cum[n]!;
  const spacing = L / n;

  // Point tangents: average of adjacent segment directions.
  const tx = new Float32Array(n);
  const tz = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const h0 = segHd[mod(i - 1, n)]!;
    const h1 = segHd[i]!;
    const h = h0 + wrapAngle(h1 - h0) / 2;
    tx[i] = Math.sin(h);
    tz[i] = Math.cos(h);
  }

  // Signed curvature (positive = right turn), smoothed over ~±30 m.
  const kRaw = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const p = mod(i - 1, n);
    const ds = (segLen[p]! + segLen[i]!) / 2 || 1;
    kRaw[i] = wrapAngle(segHd[i]! - segHd[p]!) / ds;
  }
  const sr = Math.max(1, Math.round(10 / spacing));
  const curvature = boxCircular(boxCircular(kRaw, sr), sr);

  const corners = detectCorners(curvature, cum, segLen, n, L);
  const kerbs = placeKerbs(corners, curvature, cum, n, L, spacing);

  const grid = new SegmentGrid(xs, zs, 16);
  const right = buildSide(+1, 'right');
  const left = buildSide(-1, 'left');

  function buildSide(sign: 1 | -1, side: Side): SideProfile {
    const edge = new Float32Array(n);
    for (let i = 0; i < n; i++) edge[i] = sign > 0 ? pts[i]!.widthRight : pts[i]!.widthLeft;

    const kerb = new Uint8Array(n);
    for (const k of kerbs) if (k.side === side) for (let i = k.start; i < k.end; i++) kerb[mod(i, n)] = 1;

    // Desired run-off beyond the edge.
    const asphaltW = new Float32Array(n).fill(1.5);
    const runoffW = new Float32Array(n).fill(o.straightRunoff);
    const gravel = new Uint8Array(n);
    const pad = (m: number): number => Math.round(m / spacing);
    for (const c of corners) {
      const inside = c.direction === side;
      if (inside) {
        for (let i = c.start - pad(10); i <= c.end + pad(10); i++) {
          const k = mod(i, n);
          asphaltW[k] = Math.max(asphaltW[k]!, 5);
          runoffW[k] = Math.max(runoffW[k]!, o.insideRunoff);
        }
      } else {
        const fast = c.minRadius > 70;
        const pre = pad(c.heavyBraking ? 60 : 20);
        const post = pad(50);
        const asphalt = fast ? o.outsideRunoff : c.heavyBraking ? 7 : 10;
        const runoff = c.heavyBraking ? o.outsideRunoff + 2 : o.outsideRunoff;
        for (let i = c.start - pre; i <= c.end + post; i++) {
          const k = mod(i, n);
          asphaltW[k] = Math.max(asphaltW[k]!, asphalt);
          runoffW[k] = Math.max(runoffW[k]!, runoff);
        }
        if (c.heavyBraking)
          for (let i = c.start - Math.round(pre / 2); i <= c.end + Math.round(post / 2); i++) gravel[mod(i, n)] = 1;
      }
    }
    // Paved run-off wins over gravel where another corner wants it.
    for (let i = 0; i < n; i++) if (asphaltW[i]! >= runoffW[i]! - 0.5) gravel[i] = 0;
    const tr = pad(20);
    const asphaltS = boxCircular(boxCircular(asphaltW, tr), tr);
    const runoffS = boxCircular(boxCircular(runoffW, tr), tr);

    // Free space beside each point: march the offset ray until another part
    // of the centreline gets closer than this point (the medial axis). The
    // excess d - dist(q(d)) is non-decreasing, so binary search is valid.
    const clearance = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const nx = sign * tz[i]!;
      const nz = sign * -tx[i]!;
      const crossed = (d: number): boolean =>
        grid.nearest(xs[i]! + nx * d, zs[i]! + nz * d, d) < d - (0.3 + 0.015 * d);
      let lo = 0;
      let hi = o.maxClearance;
      if (!crossed(hi)) lo = hi;
      else
        for (let it = 0; it < 14; it++) {
          const mid = (lo + hi) / 2;
          if (crossed(mid)) hi = mid;
          else lo = mid;
        }
      clearance[i] = lo;
    }

    // Barrier: desired position, clamped to the free space, smoothed without
    // ever exceeding the clamp.
    const barrierWant = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const want = edge[i]! + runoffS[i]! + o.verge;
      const lim = Math.max(edge[i]! + o.kerbWidth + 1, clearance[i]! - o.barrierMargin);
      barrierWant[i] = Math.min(want, lim);
    }
    const barrier = smoothBelow(barrierWant, pad(15));
    const asphaltOuter = new Float32Array(n);
    const runoffOuter = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const e = edge[i]!;
      const b = barrier[i]!;
      const ro = Math.max(e, Math.min(e + runoffS[i]!, b - 1));
      runoffOuter[i] = ro;
      asphaltOuter[i] = Math.max(e, Math.min(e + asphaltS[i]!, ro));
    }
    return { edge, kerb, asphaltOuter, runoffOuter, gravel, barrier, clearance };
  }

  const offsetLine = (sign: 1 | -1, d: ArrayLike<number>): Vec2[] => {
    const out: Vec2[] = new Array(n);
    for (let i = 0; i < n; i++)
      out[i] = { x: xs[i]! + sign * tz[i]! * d[i]!, z: zs[i]! + sign * -tx[i]! * d[i]! };
    return out;
  };

  const p0 = pts[0]!;
  const startLine: StartLine = {
    s: 0,
    x: p0.x,
    z: p0.z,
    heading: Math.atan2(tx[0]!, tz[0]!),
    widthLeft: p0.widthLeft,
    widthRight: p0.widthRight,
  };

  return {
    track,
    options: o,
    count: n,
    length: L,
    cum,
    tx,
    tz,
    curvature,
    corners,
    kerbs,
    right,
    left,
    startLine,
    sectors: pickSectors(curvature, cum, n, L),
    edges: { left: offsetLine(-1, left.edge), right: offsetLine(+1, right.edge) },
    barriers: {
      left: removeLoops(offsetLine(-1, left.barrier)),
      right: removeLoops(offsetLine(+1, right.barrier)),
    },
  };
}

/**
 * Corners from smoothed curvature: runs of same-sign curvature above ~1/300 m,
 * split at clear valleys between peaks, keeping those that turn >= 20 degrees.
 */
function detectCorners(k: Float32Array, cum: Float64Array, segLen: Float64Array, n: number, L: number): Corner[] {
  const kOn = 1 / 300;
  // Start scanning on a straight so no run wraps past the scan start.
  let i0 = 0;
  while (i0 < n && Math.abs(k[i0]!) >= kOn) i0++;
  if (i0 === n) i0 = 0;
  const at = (u: number): number => k[mod(u, n)]!;

  type Run = { a: number; b: number; sign: number };
  const runs: Run[] = [];
  for (let u = i0; u < i0 + n; ) {
    const v = at(u);
    if (Math.abs(v) < kOn) {
      u++;
      continue;
    }
    const sign = Math.sign(v);
    let w = u;
    while (w + 1 < i0 + n && Math.abs(at(w + 1)) >= kOn && Math.sign(at(w + 1)) === sign) w++;
    runs.push({ a: u, b: w, sign });
    u = w + 1;
  }

  const sAt = (u: number): number => cum[mod(u, n)]! + Math.floor(u / n) * L;
  const corners: Corner[] = [];
  for (const run of runs) {
    // Local maxima of |k| within the run.
    const peaks: number[] = [];
    for (let u = run.a; u <= run.b; u++) {
      const v = Math.abs(at(u));
      let isMax = true;
      for (let d = -2; d <= 2 && isMax; d++) {
        if (d === 0) continue;
        const w = u + d;
        if (w < run.a || w > run.b) continue;
        const vw = Math.abs(at(w));
        if (vw > v || (vw === v && d < 0)) isMax = false;
      }
      if (isMax) peaks.push(u);
    }
    // Merge peaks without a clear valley between them (keep the stronger).
    const kept: number[] = [];
    for (const p of peaks) {
      const q = kept[kept.length - 1];
      if (q !== undefined) {
        let valley = Infinity;
        for (let u = q; u <= p; u++) valley = Math.min(valley, Math.abs(at(u)));
        if (valley > 0.6 * Math.min(Math.abs(at(p)), Math.abs(at(q)))) {
          if (Math.abs(at(p)) > Math.abs(at(q))) kept[kept.length - 1] = p;
          continue;
        }
      }
      kept.push(p);
    }
    // Split the run at the valley minimum between consecutive kept peaks.
    const bounds = [run.a];
    for (let m = 0; m + 1 < kept.length; m++) {
      let vu = kept[m]!;
      for (let u = kept[m]!; u <= kept[m + 1]!; u++) if (Math.abs(at(u)) < Math.abs(at(vu))) vu = u;
      bounds.push(vu);
    }
    bounds.push(run.b + 1);
    kept.forEach((apex, m) => {
      const start = bounds[m]!;
      const end = bounds[m + 1]! - 1;
      let turn = 0;
      for (let u = start; u <= end; u++) turn += at(u) * segLen[mod(u, n)]!;
      const peak = Math.abs(at(apex));
      if (Math.abs(turn) < (20 * Math.PI) / 180 || peak < 1 / 250) return;
      corners.push({
        apex: mod(apex, n),
        s: cum[mod(apex, n)]!,
        start,
        end,
        direction: run.sign > 0 ? 'right' : 'left',
        minRadius: 1 / peak,
        turnAngle: Math.abs(turn),
        heavyBraking: false,
      });
    });
  }
  // Heavy braking: slow corner after a long run without a corner.
  for (let c = 0; c < corners.length; c++) {
    const cur = corners[c]!;
    const prev = corners[mod(c - 1, corners.length)]!;
    let gap = sAt(cur.start) - sAt(prev.end);
    if (gap < 0) gap += L;
    if (corners.length === 1) gap = L;
    cur.heavyBraking = cur.minRadius < 50 && gap > 200;
  }
  // Normalise so start lies in [0, n).
  for (const c of corners) {
    const shift = Math.floor(c.start / n) * n;
    c.start -= shift;
    c.end -= shift;
  }
  return corners.sort((a, b) => a.start - b.start);
}

/** Apex kerbs on the inside around peak curvature, exit kerbs on the outside from apex to past the corner. */
function placeKerbs(
  corners: Corner[],
  k: Float32Array,
  cum: Float64Array,
  n: number,
  L: number,
  spacing: number,
): KerbStrip[] {
  const pad = (m: number): number => Math.max(1, Math.round(m / spacing));
  const raw: { side: Side; kind: 'apex' | 'exit'; start: number; end: number }[] = [];
  for (const c of corners) {
    if (c.minRadius > 300) continue;
    const peak = 1 / c.minRadius;
    // Apex position in the corner's unwrapped index space.
    let apex = c.apex;
    while (apex < c.start) apex += n;
    let a0 = apex;
    let a1 = apex;
    // Long sweepers get an apex kerb of at most ~120 m around the apex.
    const maxHalf = pad(60);
    while (a0 > c.start && apex - a0 < maxHalf && Math.abs(k[mod(a0 - 1, n)]!) >= 0.55 * peak) a0--;
    while (a1 < c.end && a1 - apex < maxHalf && Math.abs(k[mod(a1 + 1, n)]!) >= 0.55 * peak) a1++;
    const inside: Side = c.direction;
    const outside: Side = inside === 'right' ? 'left' : 'right';
    raw.push({ side: inside, kind: 'apex', start: a0 - pad(8), end: a1 + pad(8) });
    if (c.turnAngle > (25 * Math.PI) / 180) {
      const exitStart = apex + Math.round((a1 - apex) / 2);
      const exitEnd = Math.max(c.end, a1) + pad(c.minRadius < 60 ? 25 : 35);
      raw.push({ side: outside, kind: 'exit', start: exitStart, end: exitEnd });
    }
  }
  // Merge overlapping strips on the same side (in circular index space).
  const out: KerbStrip[] = [];
  for (const side of ['left', 'right'] as const) {
    const list = raw
      .filter((r) => r.side === side)
      .map((r) => {
        const sh = Math.floor(r.start / n) * n;
        return { ...r, start: r.start - sh, end: r.end - sh };
      })
      .sort((a, b) => a.start - b.start);
    const merged: typeof list = [];
    for (const r of list) {
      const last = merged[merged.length - 1];
      if (last && r.start <= last.end + pad(6)) {
        last.end = Math.max(last.end, r.end);
        if (r.kind === 'apex') last.kind = 'apex';
      } else merged.push({ ...r });
    }
    // Wrap-around merge between the last and first strip.
    if (merged.length > 1) {
      const first = merged[0]!;
      const last = merged[merged.length - 1]!;
      if (last.end >= first.start + n - pad(6)) {
        last.end = Math.max(last.end, first.end + n);
        merged.shift();
      }
    }
    for (const r of merged) {
      const end = Math.min(r.end, r.start + n);
      const s0 = cum[mod(r.start, n)]!;
      const s1 = cum[mod(end, n)]! + Math.floor(end / n) * L - Math.floor(r.start / n) * L;
      out.push({ side, kind: r.kind, start: r.start, end, s0, length: s1 - s0 });
    }
  }
  return out.sort((a, b) => a.start - b.start);
}

/** Sector boundaries: the middle of the straight nearest each third of the lap. */
function pickSectors(k: Float32Array, cum: Float64Array, n: number, L: number): [number, number] {
  const straight = (i: number): boolean => Math.abs(k[i]!) < 1 / 500;
  // Collect straights (runs of low curvature at least 60 m long), as [sStart, sEnd].
  const runs: [number, number][] = [];
  let i = 0;
  while (i < n) {
    if (!straight(i)) {
      i++;
      continue;
    }
    let j = i;
    while (j + 1 < n && straight(j + 1)) j++;
    if (cum[j + 1]! - cum[i]! >= 60) runs.push([cum[i]!, cum[j + 1]!]);
    i = j + 1;
  }
  const pick = (target: number): number => {
    let best = target;
    let bestD = Infinity;
    for (const [a, b] of runs) {
      // Keep away from the start line and stay inside the run with a margin.
      const lo = a + Math.min(30, (b - a) / 3);
      const hi = b - Math.min(30, (b - a) / 3);
      const sPos = Math.min(hi, Math.max(lo, target));
      const d = Math.abs(sPos - target);
      if (d < bestD) {
        bestD = d;
        best = sPos;
      }
    }
    return bestD < L / 8 ? best : target;
  };
  return [pick(L / 3), pick((2 * L) / 3)];
}

// ---------------------------------------------------------------------------
// Queries used per physics step

function segmentT(l: TrackLayout, p: TrackProjection): number {
  const i = p.index;
  const seg = l.cum[i + 1]! - l.cum[i]!;
  return seg > 0 ? Math.max(0, Math.min(1, (p.s - l.cum[i]!) / seg)) : 0;
}

/** Surface under a projected position. O(1); safe to call every physics step. */
export function surfaceAt(track: Track, p: TrackProjection): Surface {
  const l = getLayout(track);
  const right = p.offset >= 0;
  const a = right ? p.offset : -p.offset;
  const edge = right ? p.widthRight : p.widthLeft;
  if (a <= edge) return 'track';
  const side = right ? l.right : l.left;
  const i = p.index;
  if (side.kerb[i] && a <= edge + l.options.kerbWidth) return 'kerb';
  const j = i + 1 === l.count ? 0 : i + 1;
  const t = segmentT(l, p);
  const lerp = (arr: Float32Array): number => arr[i]! + (arr[j]! - arr[i]!) * t;
  if (a <= lerp(side.asphaltOuter)) return 'runoff-asphalt';
  if (a <= lerp(side.runoffOuter)) return side.gravel[i] ? 'gravel' : 'grass';
  if (a <= lerp(side.barrier)) return 'grass';
  return 'outside';
}

/** Lateral distance from the centreline to the barrier on the side of the projection (positive, metres). */
export function barrierOffsetAt(track: Track, p: TrackProjection): number {
  const l = getLayout(track);
  const side = p.offset >= 0 ? l.right : l.left;
  const i = p.index;
  const j = i + 1 === l.count ? 0 : i + 1;
  const t = segmentT(l, p);
  return side.barrier[i]! + (side.barrier[j]! - side.barrier[i]!) * t;
}

/** Sector (0, 1 or 2) for a distance along the lap. */
export function sectorAt(track: Track, s: number): 0 | 1 | 2 {
  const [s1, s2] = getLayout(track).sectors;
  return s < s1 ? 0 : s < s2 ? 1 : 2;
}

// ---------------------------------------------------------------------------
// Mesh generation

class MeshBuilder {
  pos: number[] = [];
  nor: number[] = [];
  uv: number[] = [];
  col: number[] | null = null;
  idx: number[] = [];

  vertex(x: number, y: number, z: number, nx: number, ny: number, nz: number, u: number, v: number): number {
    this.pos.push(x, y, z);
    this.nor.push(nx, ny, nz);
    this.uv.push(u, v);
    return this.pos.length / 3 - 1;
  }

  color(r: number, g: number, b: number): void {
    (this.col ??= []).push(r, g, b, 1);
  }

  /** Triangle wound so that its front faces along (nx, ny, nz) in Babylon's convention. */
  tri(a: number, b: number, c: number, nx: number, ny: number, nz: number): void {
    const P = this.pos;
    const ux = P[3 * b]! - P[3 * a]!;
    const uy = P[3 * b + 1]! - P[3 * a + 1]!;
    const uz = P[3 * b + 2]! - P[3 * a + 2]!;
    const vx = P[3 * c]! - P[3 * a]!;
    const vy = P[3 * c + 1]! - P[3 * a + 1]!;
    const vz = P[3 * c + 2]! - P[3 * a + 2]!;
    const cx = uy * vz - uz * vy;
    const cy = uz * vx - ux * vz;
    const cz = ux * vy - uy * vx;
    // Babylon's builders (e.g. CreateGround) produce (b-a)x(c-a) opposite the front normal.
    if (cx * nx + cy * ny + cz * nz > 0) this.idx.push(a, c, b);
    else this.idx.push(a, b, c);
  }

  quad(a: number, b: number, c: number, d: number, nx = 0, ny = 1, nz = 0): void {
    this.tri(a, b, c, nx, ny, nz);
    this.tri(a, c, d, nx, ny, nz);
  }

  build(): MeshData {
    const m: MeshData = {
      positions: new Float32Array(this.pos),
      normals: new Float32Array(this.nor),
      indices: new Uint32Array(this.idx),
      uvs: new Float32Array(this.uv),
    };
    if (this.col) m.colors = new Float32Array(this.col);
    return m;
  }
}

/** Generate all track meshes, grouped by material. */
export function buildTrackGeometry(track: Track): TrackGeometry {
  const l = getLayout(track);
  const { count: n, cum, tx, tz, options: o } = l;
  const pts = track.points;

  const at = (i: number, signedOffset: number): [number, number] => {
    const p = pts[i]!;
    return [p.x + tz[i]! * signedOffset, p.z - tx[i]! * signedOffset];
  };

  /**
   * Strip between two signed offsets along the whole lap (or the segments
   * where include(i) is true). Vertices are shared within a run.
   */
  const band = (
    mb: MeshBuilder,
    y: number,
    off0: (i: number) => number,
    off1: (i: number) => number,
    include: (i: number) => boolean = () => true,
  ): void => {
    let prev: [number, number] | null = null;
    for (let i = 0; i < n; i++) {
      if (!include(i)) {
        prev = null;
        continue;
      }
      const j = i + 1 === n ? 0 : i + 1;
      const vAt = (k: number, sAlong: number): [number, number] => {
        const o0 = off0(k);
        const o1 = off1(k);
        const [x0, z0] = at(k, o0);
        const [x1, z1] = at(k, o1);
        return [mb.vertex(x0, y, z0, 0, 1, 0, o0, sAlong), mb.vertex(x1, y, z1, 0, 1, 0, o1, sAlong)];
      };
      const a = prev ?? vAt(i, cum[i]!);
      const b = vAt(j, cum[i + 1]!);
      mb.quad(a[0], a[1], b[1], b[0]);
      prev = b;
    }
  };

  const R = l.right;
  const Lf = l.left;

  // Road.
  const road = new MeshBuilder();
  band(
    road,
    LAYER_Y.road,
    (i) => -Lf.edge[i]!,
    (i) => R.edge[i]!,
  );

  // White edge lines and the start/finish line.
  const lines = new MeshBuilder();
  band(
    lines,
    LAYER_Y.lines,
    (i) => R.edge[i]! - o.lineWidth,
    (i) => R.edge[i]!,
  );
  band(
    lines,
    LAYER_Y.lines,
    (i) => -Lf.edge[i]!,
    (i) => -Lf.edge[i]! + o.lineWidth,
  );
  {
    const sl = l.startLine;
    const half = 0.4;
    const nx = Math.cos(sl.heading); // right normal = (tz, -tx) with t = (sin h, cos h)
    const nz = -Math.sin(sl.heading);
    const fx = Math.sin(sl.heading);
    const fz = Math.cos(sl.heading);
    const v = (lat: number, along: number): number =>
      lines.vertex(sl.x + nx * lat + fx * along, LAYER_Y.lines, sl.z + nz * lat + fz * along, 0, 1, 0, lat, along);
    const a = v(-sl.widthLeft, -half);
    const b = v(sl.widthRight, -half);
    const c = v(sl.widthRight, half);
    const d = v(-sl.widthLeft, half);
    lines.quad(a, b, c, d);
  }

  // Kerbs: flat strips with alternating red/white blocks (duplicated vertices per block).
  const kerbs = new MeshBuilder();
  for (const strip of l.kerbs) {
    const sign = strip.side === 'right' ? 1 : -1;
    const prof = strip.side === 'right' ? R : Lf;
    const s0 = strip.s0;
    const sEnd = s0 + strip.length;
    // Sample positions: every centreline point plus each block boundary.
    const samples: number[] = [];
    for (let u = strip.start; u <= strip.end; u++) samples.push(cum[mod(u, n)]! + Math.floor(u / n) * l.length);
    for (let s = s0 + o.kerbBlock; s < sEnd - 1e-6; s += o.kerbBlock) samples.push(s);
    samples.sort((a, b) => a - b);
    const frame = (sU: number): { x: number; z: number; nx: number; nz: number; w: number } => {
      const s = mod(sU, l.length);
      // Binary search the segment.
      let lo = 0;
      let hi = n - 1;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (cum[mid]! <= s) lo = mid;
        else hi = mid - 1;
      }
      const j = lo + 1 === n ? 0 : lo + 1;
      const seg = cum[lo + 1]! - cum[lo]!;
      const t = seg > 0 ? (s - cum[lo]!) / seg : 0;
      const a = pts[lo]!;
      const b = pts[j]!;
      let nx = tz[lo]! + (tz[j]! - tz[lo]!) * t;
      let nz = -(tx[lo]! + (tx[j]! - tx[lo]!) * t);
      const nl = Math.hypot(nx, nz) || 1;
      nx /= nl;
      nz /= nl;
      return {
        x: a.x + (b.x - a.x) * t,
        z: a.z + (b.z - a.z) * t,
        nx: nx * sign,
        nz: nz * sign,
        w: prof.edge[lo]! + (prof.edge[j]! - prof.edge[lo]!) * t,
      };
    };
    for (let m = 0; m + 1 < samples.length; m++) {
      const sa = samples[m]!;
      const sb = samples[m + 1]!;
      if (sb - sa < 1e-3) continue;
      const block = Math.floor(((sa + sb) / 2 - s0) / o.kerbBlock);
      const rgb = block % 2 === 0 ? KERB_RED : KERB_WHITE;
      const fa = frame(sa);
      const fb = frame(sb);
      const va = (sa - s0) / o.kerbBlock;
      const vb = (sb - s0) / o.kerbBlock;
      const y = LAYER_Y.kerbs;
      const w = o.kerbWidth;
      const i0 = kerbs.vertex(fa.x + fa.nx * fa.w, y, fa.z + fa.nz * fa.w, 0, 1, 0, 0, va);
      const i1 = kerbs.vertex(fa.x + fa.nx * (fa.w + w), y, fa.z + fa.nz * (fa.w + w), 0, 1, 0, 1, va);
      const i2 = kerbs.vertex(fb.x + fb.nx * (fb.w + w), y, fb.z + fb.nz * (fb.w + w), 0, 1, 0, 1, vb);
      const i3 = kerbs.vertex(fb.x + fb.nx * fb.w, y, fb.z + fb.nz * fb.w, 0, 1, 0, 0, vb);
      for (let k = 0; k < 4; k++) kerbs.color(rgb[0], rgb[1], rgb[2]);
      kerbs.quad(i0, i1, i2, i3);
    }
  }

  // Run-off: asphalt from the edge (under the kerbs), then grass to the
  // barrier with gravel traps laid over the grass.
  const runoffAsphalt = new MeshBuilder();
  const grass = new MeshBuilder();
  const gravel = new MeshBuilder();
  for (const [sign, prof] of [
    [1, R],
    [-1, Lf],
  ] as const) {
    band(
      runoffAsphalt,
      LAYER_Y.runoffAsphalt,
      (i) => sign * prof.edge[i]!,
      (i) => sign * prof.asphaltOuter[i]!,
    );
    band(
      grass,
      LAYER_Y.grass,
      (i) => sign * prof.asphaltOuter[i]!,
      (i) => sign * prof.barrier[i]!,
    );
    band(
      gravel,
      LAYER_Y.gravel,
      (i) => sign * prof.asphaltOuter[i]!,
      (i) => sign * prof.runoffOuter[i]!,
      (i) => prof.gravel[i] === 1,
    );
  }

  // Barriers: vertical walls along the cleaned polylines, facing the track.
  const barriers = new MeshBuilder();
  for (const [poly, toTrack] of [
    [l.barriers.right, 1],
    [l.barriers.left, -1],
  ] as const) {
    const m = poly.length;
    // Vertex normals: average of adjacent segment normals pointing at the track.
    const vn: [number, number][] = [];
    const segN = (k: number): [number, number] => {
      const a = poly[k]!;
      const b = poly[(k + 1) % m]!;
      const len = Math.hypot(b.x - a.x, b.z - a.z) || 1;
      const dx = (b.x - a.x) / len;
      const dz = (b.z - a.z) / len;
      // Track is to the left of a right-side barrier: left normal (-dz, dx).
      return toTrack > 0 ? [-dz, dx] : [dz, -dx];
    };
    for (let k = 0; k < m; k++) {
      const a = segN(mod(k - 1, m));
      const b = segN(k);
      const len = Math.hypot(a[0] + b[0], a[1] + b[1]) || 1;
      vn.push([(a[0] + b[0]) / len, (a[1] + b[1]) / len]);
    }
    // UV u runs along the wall; it's negated on the right-hand wall so text on
    // the boards reads left-to-right from the track on both sides.
    let along = 0;
    let prevPair: [number, number] | null = null;
    for (let k = 0; k <= m; k++) {
      const kk = k % m;
      const p = poly[kk]!;
      if (k > 0) {
        const q = poly[k - 1]!;
        along += Math.hypot(p.x - q.x, p.z - q.z);
      }
      let pair: [number, number];
      if (k === m) {
        // Close the loop with fresh vertices (UV seam).
        const [nx, nz] = vn[0]!;
        pair = [
          barriers.vertex(p.x, 0, p.z, nx, 0, nz, -along * toTrack, 0),
          barriers.vertex(p.x, o.barrierHeight, p.z, nx, 0, nz, -along * toTrack, o.barrierHeight),
        ];
      } else {
        const [nx, nz] = vn[kk]!;
        pair = [
          barriers.vertex(p.x, 0, p.z, nx, 0, nz, -along * toTrack, 0),
          barriers.vertex(p.x, o.barrierHeight, p.z, nx, 0, nz, -along * toTrack, o.barrierHeight),
        ];
      }
      if (prevPair) {
        const sn = segN(k - 1);
        barriers.quad(prevPair[0], pair[0], pair[1], prevPair[1], sn[0], 0, sn[1]);
      }
      prevPair = pair;
    }
  }

  // Bounds over everything (barriers are outermost).
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of [...l.barriers.left, ...l.barriers.right, ...l.edges.left, ...l.edges.right]) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
  }

  return {
    road: road.build(),
    lines: lines.build(),
    kerbs: kerbs.build(),
    runoffAsphalt: runoffAsphalt.build(),
    grass: grass.build(),
    gravel: gravel.build(),
    barriers: barriers.build(),
    bounds: { minX, maxX, minZ, maxZ },
  };
}
